import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Search, Globe } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
      setActiveDropdown(null);
    }
  };

  const menuItems = [
    {
      label: 'Soluciones',
      hasDropdown: true,
      items: [
        { label: 'Desarrollo de Software', action: () => scrollToSection('solutions') },
        { label: 'Gestión de Nubes', action: () => scrollToSection('solutions') },
        { label: 'Inteligencia Artificial', action: () => scrollToSection('solutions') }
      ]
    },
    {
      label: 'Servicios',
      hasDropdown: true,
      items: [
        { label: 'Consultoría', action: () => scrollToSection('benefits') },
        { label: 'Implementación', action: () => scrollToSection('benefits') },
        { label: 'Soporte', action: () => scrollToSection('contact') }
      ]
    },
    {
      label: 'Casos de Éxito',
      hasDropdown: false,
      action: () => scrollToSection('cases')
    },
    {
      label: 'Conocernos',
      hasDropdown: true,
      items: [
        { label: 'Sobre Nosotros', action: () => scrollToSection('hero') },
        { label: 'Tecnologías', action: () => scrollToSection('technologies') },
        { label: 'Equipo', action: () => scrollToSection('hero') }
      ]
    }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white/80 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button 
              onClick={() => scrollToSection('hero')} 
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_eco-cloud-expert/artifacts/2spz5f2a_cexp.png" 
                alt="CloudExpert Logo" 
                className="h-10 w-auto"
              />
            </button>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-1">
            {menuItems.map((item, index) => (
              <div
                key={index}
                className="relative"
                onMouseEnter={() => item.hasDropdown && setActiveDropdown(index)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {item.hasDropdown ? (
                  <>
                    <button className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === index ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {activeDropdown === index && (
                      <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-fade-in">
                        {item.items.map((subItem, subIndex) => (
                          <button
                            key={subIndex}
                            onClick={subItem.action}
                            className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 hover:text-[#ff6b35] transition-colors"
                          >
                            {subItem.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    onClick={item.action}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    {item.label}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Right Section */}
          <div className="hidden lg:flex items-center gap-3">
            <button className="p-2 text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100">
              <Search className="w-5 h-5" />
            </button>
            <button className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100">
              <Globe className="w-4 h-4" />
              <span>ES</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            <button 
              onClick={() => scrollToSection('contact')} 
              className="bg-gradient-to-r from-[#ff6b35] to-[#f97316] hover:from-[#f97316] hover:to-[#ff6b35] text-white px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/30"
            >
              Contáctanos
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-900 hover:text-[#ff6b35] transition-colors p-2"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {menuItems.map((item, index) => (
              <div key={index}>
                {item.hasDropdown ? (
                  <>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                      className="flex items-center justify-between w-full text-left px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === index ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === index && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.items.map((subItem, subIndex) => (
                          <button
                            key={subIndex}
                            onClick={subItem.action}
                            className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            {subItem.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    onClick={item.action}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    {item.label}
                  </button>
                )}
              </div>
            ))}
            <div className="pt-3 border-t border-gray-200 mt-3">
              <button 
                onClick={() => scrollToSection('contact')} 
                className="w-full bg-gradient-to-r from-[#ff6b35] to-[#f97316] text-white px-6 py-3 rounded-full text-sm font-medium transition-all duration-300"
              >
                Contáctanos
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;